import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LOGOUT_LOGO_DATA_URI } from "@/assets/logoutLogoDataUri";

const LOGOUT_LOGO_PRELOAD_SRC = "/logo-brand.webp";

/**
 * Overlay animado exibido durante o logout.
 * Paleta alinhada à tela de login (lavender + gradiente #7c3aed → #a855f7).
 */
export function LogoutOverlay() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Pré-carrega e força o decode da logo assim que o app monta.
    // A mesma URL também fica em um <img> sempre montado abaixo,
    // eliminando a requisição/decodificação no momento do clique em sair.
    const preload = new Image();
    preload.decoding = "sync";
    preload.src = LOGOUT_LOGO_DATA_URI;
    void preload.decode?.().catch(() => undefined);

    const preloadExternal = new Image();
    preloadExternal.src = LOGOUT_LOGO_PRELOAD_SRC;

    const onStart = () => setVisible(true);
    const onEnd = () => setVisible(false);
    window.addEventListener("lovable:logout-start", onStart);
    window.addEventListener("lovable:logout-end", onEnd);
    return () => {
      window.removeEventListener("lovable:logout-start", onStart);
      window.removeEventListener("lovable:logout-end", onEnd);
    };
  }, []);

  return (
    <motion.div
      aria-hidden={!visible}
      initial={false}
      animate={{ opacity: visible ? 1 : 0 }}
      transition={{ duration: 0.28, ease: "easeOut" }}
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#f5efff] via-[#efe6ff] to-[#e9dcff]"
      style={{ pointerEvents: visible ? "auto" : "none", visibility: visible ? "visible" : "hidden" }}
    >
          {/* Blobs decorativos (mesma vibe do login) */}
          <motion.div
            aria-hidden
            className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(168,85,247,0.35), transparent 60%)",
            }}
            animate={{ scale: [1, 1.15, 1], opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            aria-hidden
            className="pointer-events-none absolute -bottom-32 -right-32 h-[28rem] w-[28rem] rounded-full"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(124,58,237,0.28), transparent 60%)",
            }}
            animate={{ scale: [1.1, 1, 1.1], opacity: [0.6, 0.9, 0.6] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          />

          {/* Card central */}
          <motion.div
            initial={false}
            animate={visible ? { scale: 1, opacity: 1, y: 0 } : { scale: 0.96, opacity: 0, y: 10 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 flex flex-col items-center gap-6 rounded-3xl border border-white/60 bg-white/70 px-10 py-10 shadow-2xl shadow-purple-500/10 backdrop-blur-xl"
            style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
          >
            {/* Logo real com halo pulsante */}
            <div className="relative flex items-center justify-center">
              <motion.div
                aria-hidden
                className="absolute inset-0 -m-4 rounded-full bg-gradient-to-r from-[#7c3aed] to-[#a855f7] opacity-20 blur-xl"
                animate={{ scale: [1, 1.2, 1], opacity: [0.15, 0.35, 0.15] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
              />
              <img
                src={LOGOUT_LOGO_DATA_URI}
                alt="Beauty Gestão"
                width={160}
                height={80}
                decoding="sync"
                loading="eager"
                className="relative h-20 w-auto object-contain"
              />
            </div>

            <div className="text-center">
              <h2 className="text-xl font-semibold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-[#7c3aed] to-[#a855f7]">
                Encerrando sua sessão
              </h2>
              <p
                className="mt-1.5 text-sm text-gray-600"
                style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
              >
                Até breve! Estamos te levando para o login…
              </p>
            </div>

            {/* Barra de progresso indeterminada */}
            <div className="mt-1 h-1 w-56 overflow-hidden rounded-full bg-purple-100">
              <motion.div
                className="h-full w-1/3 rounded-full bg-gradient-to-r from-[#7c3aed] to-[#a855f7]"
                animate={{ x: ["-100%", "300%"] }}
                transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
              />
            </div>
          </motion.div>
    </motion.div>
  );
}
